// NumberFormatter utility implementing currency (USDC), token prices, sizes, and percents
//
// Every displayed number must use this utility

export interface USDCOptions {
  decimals?: number;
  symbol?: boolean;
  explicitPlus?: boolean;
}
export interface PriceOptions {
  pxDecimals?: number;
  symbol?: boolean;
}

export interface SizeOptions {
  szDecimals?: number;
  /** Omit padding zeros after the decimal separator ("1.5" rather than "1.5000"). */
  trimTrailingZeros?: boolean;
}

export interface CompactOptions {
  currency?: boolean;
  minimumFractionDigits?: number;
  locale?: string;
  /** Uppercase the magnitude suffix (e.g. "4.8T" instead of the default "4.8t"). */
  uppercase?: boolean;
}

export interface PercentOptions {
  isFraction?: boolean;
  /** Use the same precision for both small and large values */
  fixedPrecision?: boolean;
  /**
   * Exact number of decimal places, overriding both the default precision and `fixedPrecision`.
   * For percentages that are a share of something the user chose rather than a rate — where the
   * decimals are noise, not signal. `0` renders "90%" rather than "90.00%".
   */
  decimals?: number;
  explicitPlus?: boolean;
}

export type NumberFormatterType =
  | "usdc"
  | "price"
  | "size"
  | "compact"
  | "compactHeading"
  | "percent"
  | "ratio";

const DEFAULT_SIGNIFICANT_DIGITS = 5;
const DEFAULT_SZ_DECIMALS = 8;
const DEFAULT_PX_DECIMALS = 8;

const USD_DECIMALS = 2;
const COMPACT_DECIMALS = 1;
const COMPACT_HEADING_DECIMALS = 2;

const PERCENT_DECIMALS_LT_ONE = 4; // for percent values < 1%
const PERCENT_DECIMALS_GE_ONE = 2; // for percent values >= 1%
const PERCENT_MIN_NONZERO = 0.0001; // display floor at 0.0001% for non-zero values

const getSign = (value: number, explicitPlus?: boolean) =>
  value > 0 && explicitPlus ? "+" : value >= 0 ? "" : "-";

const prefixWithSign = (sign: string, content: string) => `${sign}${content}`;

const formatCurrencyWithFixedDigits = (
  value: number,
  minDecimals: number,
  maxDecimals: number,
  symbol: boolean,
) => {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
    style: symbol ? "currency" : undefined,
    currency: symbol ? "USD" : undefined,
  });
};

const formatNumberWithFixedDigits = (value: number, minDecimals: number, maxDecimals: number) => {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
  });
};

const roundToDecimalPlaces = (value: number, decimals: number) => {
  return Math.round(value * 10 ** decimals) / 10 ** decimals;
};

const formatPercentWithFixedDigits = (value: number, minDecimals: number, maxDecimals: number) => {
  return value.toLocaleString(undefined, {
    minimumFractionDigits: minDecimals,
    maximumFractionDigits: maxDecimals,
    style: "percent",
  });
};

const getIntegerDigitsLength = (value: number) => {
  return Math.floor(value).toString().length;
};

const getLeadingZerosLengthAfterDecimal = (value: number | string): number => {
  const n = typeof value === "string" ? Number(value) : value;
  if (!Number.isFinite(n) || n === 0) return 0;

  // Always get scientific notation so we can read the exponent reliably.
  const expMatch = /e([+-]\d+)$/i.exec(Math.abs(n).toExponential());
  const exp = expMatch ? parseInt(expMatch[1] ?? "0", 10) : 0;

  // For numbers < 1, exponent is negative.
  // # of zeros after decimal before first significant digit = -exp - 1.
  return exp < 0 ? -exp - 1 : 0;
};

const formatChainUnits = (
  value: number,
  decimals: number,
  symbol?: boolean,
  trimTrailingZeros?: boolean,
): string => {
  if (!Number.isFinite(value)) return String(value);
  const sign = getSign(value);
  const absValue = Math.abs(value);
  const prefixWithSymbol = (v: string): string => (symbol ? `$${v}` : v);

  if (absValue === 0) {
    if (decimals === 0 || trimTrailingZeros) {
      return prefixWithSymbol("0");
    }
    if (decimals < 4) {
      return prefixWithSymbol(`0.${"0".repeat(decimals - 1)}0`);
    }
    return prefixWithSymbol("0.0000");
  }

  const digitsLength = getIntegerDigitsLength(absValue);
  const leadingZerosLength = getLeadingZerosLengthAfterDecimal(absValue);
  let decimalsNeeded = Math.min(
    decimals,
    DEFAULT_SIGNIFICANT_DIGITS - digitsLength + leadingZerosLength,
  );
  decimalsNeeded = Math.max(0, decimalsNeeded);

  const rounded = roundToDecimalPlaces(absValue, decimalsNeeded);

  // If a very small value rounds to zero, we default to the nearest non-zero value
  if (rounded === 0) {
    if (decimalsNeeded === 0) {
      return prefixWithSign(sign, prefixWithSymbol("0"));
    }
    return prefixWithSign(sign, prefixWithSymbol(`0.${"0".repeat(decimalsNeeded - 1)}1`));
  }

  const content = formatNumberWithFixedDigits(
    rounded,
    trimTrailingZeros ? 0 : decimalsNeeded,
    decimalsNeeded,
  );
  return prefixWithSign(sign, prefixWithSymbol(content));
};

export const NumberFormatter = {
  usdc(value: number, options?: USDCOptions): string {
    const { symbol = true, decimals = USD_DECIMALS, explicitPlus } = options ?? {};
    if (!Number.isFinite(value)) return String(value);
    const sign = getSign(value, explicitPlus);
    const absValue = Math.abs(value);

    const content = formatCurrencyWithFixedDigits(absValue, decimals, decimals, symbol);
    return prefixWithSign(sign, content);
  },

  price(value: number, options?: PriceOptions): string {
    const { pxDecimals = DEFAULT_PX_DECIMALS, symbol = false } = options ?? {};
    return formatChainUnits(value, pxDecimals, symbol);
  },

  size(value: number, options?: SizeOptions): string {
    const { szDecimals = DEFAULT_SZ_DECIMALS, trimTrailingZeros } = options ?? {};
    return formatChainUnits(value, szDecimals, false, trimTrailingZeros);
  },

  percent(value: number, options?: PercentOptions): string {
    const { isFraction = true, fixedPrecision = false, decimals, explicitPlus } = options ?? {};
    if (!Number.isFinite(value)) return String(value);
    const sign = getSign(value, explicitPlus);
    const absValue = Math.abs(value);

    const decimalsNeeded =
      decimals ??
      (fixedPrecision
        ? PERCENT_DECIMALS_GE_ONE
        : absValue < 0.01
          ? PERCENT_DECIMALS_LT_ONE
          : PERCENT_DECIMALS_GE_ONE);

    if (absValue === 0) {
      // The 4-decimal default exists so a sub-1% funding rate doesn't round to nothing; a caller
      // that asked for a coarser precision is displaying a figure where the extra digits are
      // noise, and "0.0000%" reads as a rounding artifact rather than a plain zero.
      return decimals == null && !fixedPrecision
        ? "0.0000%"
        : formatPercentWithFixedDigits(0, decimalsNeeded, decimalsNeeded);
    }

    // Add two decimals to account for division by 100
    let rounded = roundToDecimalPlaces(absValue, decimalsNeeded + 2);

    if (rounded === 0) {
      // Ensure floor uses the same displayed decimal count via percent formatting
      const floorAsFraction = PERCENT_MIN_NONZERO / 100;
      const content = formatPercentWithFixedDigits(floorAsFraction, decimalsNeeded, decimalsNeeded);
      return prefixWithSign(sign, content);
    }

    if (!isFraction) {
      rounded = rounded / 100; // Convert to fraction if not a fraction, assuming its a percentage
    }

    const content = formatPercentWithFixedDigits(rounded, decimalsNeeded, decimalsNeeded);
    return prefixWithSign(sign, content);
  },

  compact(value: number, options?: CompactOptions): string {
    const formatted = value.toLocaleString(options?.locale, {
      notation: "compact",
      style: options?.currency ? "currency" : undefined,
      currency: options?.currency ? "USD" : undefined, // TODO: allow custom currency
      minimumFractionDigits: options?.minimumFractionDigits ?? COMPACT_DECIMALS,
      maximumFractionDigits: COMPACT_DECIMALS,
    });
    return options?.uppercase ? formatted.toUpperCase() : formatted.toLowerCase();
  },

  compactHeading(value: number, options?: CompactOptions): string {
    const formatted = value.toLocaleString(options?.locale, {
      notation: "compact",
      style: options?.currency ? "currency" : undefined,
      currency: options?.currency ? "USD" : undefined, // TODO: allow custom currency
      minimumFractionDigits: COMPACT_HEADING_DECIMALS,
      maximumFractionDigits: COMPACT_HEADING_DECIMALS,
    });
    return formatted.toUpperCase();
  },

  ratio(value: number, decimals = 2): string {
    if (!Number.isFinite(value)) return String(value);
    const sign = getSign(value);
    const absValue = Math.abs(value);
    const content = formatNumberWithFixedDigits(absValue, decimals, decimals);
    return prefixWithSign(sign, content);
  },
} as const;
